// Student CertificatesPage — earned certificates with a download action.
import { useState } from "react";
import { useFetch, getJson } from "../../hooks/useApi.js";
import type { Certificate } from "@dm-lms/shared/types";

export default function CertificatesPage() {
  const { data, isLoading, error } = useFetch<Certificate[]>("/api/certificates/mine");
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function download(certificateId: string) {
    setDownloadingId(certificateId);
    setDownloadError(null);

    const result = await getJson<{ url: string; expiresAt: string }>(`/api/certificates/${certificateId}/download`);
    setDownloadingId(null);
    if (!result.ok) {
      setDownloadError(result.error);
      return;
    }
    window.open(result.data.url, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Certificates</h1>

      {isLoading && <p className="text-dm-slate text-sm">Loading certificates…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}
      {downloadError && <p className="text-red-600 text-sm mb-3">{downloadError}</p>}

      {!isLoading && !error && (
        (data ?? []).length === 0 ? (
          <p className="text-dm-slate text-sm">No certificates yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
            {(data ?? []).map((certificate) => (
              <li key={certificate.id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-dm-navy">{certificate.code}</p>
                  <p className="text-sm text-dm-slate">
                    Issued {new Date(certificate.issuedAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  type="button"
                  className="bg-dm-blue text-white text-sm px-4 py-2 rounded-md disabled:opacity-50"
                  disabled={downloadingId === certificate.id}
                  onClick={() => download(certificate.id)}
                >
                  {downloadingId === certificate.id ? "Preparing…" : "Download"}
                </button>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}
